import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IEntityDTOIn, IEntityFromBack } from '../interfaces/groups/entity.interface';
import { ISchool, ISchoolDTOIn, ISchoolDTOOut } from '../interfaces/groups/school.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class SchoolsService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // READ
    public getAllSchools(): Observable<ISchool[]> {
        return this.http
            .get<IEntityFromBack>(environment.wasoApi + SubUrlsEnum.SCHOOLS)
            .pipe(
                map((result: IEntityFromBack) =>
                    result.schools.map((school: IEntityDTOIn) =>
                        this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(school))
                    )
                )
            );
    }

    public getSchoolById(id: number): Observable<ISchool> {
        return this.http
            .get<IEntityDTOIn>(environment.wasoApi + SubUrlsEnum.SCHOOL + id)
            .pipe(
                map((school: IEntityDTOIn) => this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(school)))
            );
    }

    // CREATE
    public createSchool(newSchool: ISchoolDTOOut): Observable<ISchool> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<ISchoolDTOIn>(environment.wasoApi + SubUrlsEnum.SCHOOLS, newSchool, { headers })
            .pipe(
                map((school: IEntityDTOIn) => this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(school)))
            );
    }

    // UPDATE
    public patchSchool(school: ISchoolDTOOut, id: number): Observable<ISchool> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<ISchoolDTOIn>(environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + id, school, { headers })
            .pipe(
                map((entity: ISchoolDTOIn) => this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    // DELETE
    public deleteSchool(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + id, {
            headers,
        });
    }

    // cruD: DELETE -> ENABLE / DISABLE
    public enableSchool(schoolId: number): Observable<ISchool> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + schoolId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .post<IEntityDTOIn>(fullUrl, {}, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    public disableSchool(schoolId: number): Observable<ISchool> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + schoolId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .delete<IEntityDTOIn>(fullUrl, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToISchool(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    public uploadSignature(id: number, file: File): Observable<any> {
        const httpOptions: any = {
            headers: new HttpHeaders({
                ['Content-Type']: 'multipart/form-data',
            }),
        };
        const formData: FormData = new FormData();
        formData.append('school_setting[file]', file);
        return this.http.post<any>(
            environment.wasoApi + SubUrlsEnum.SCHOOL_SIGNATURE.replace('{{id}}', id.toString()),
            formData,
            httpOptions
        );
    }
}
